import { Typography } from "@shared/ui.primitives";

import { Marker } from "./Marker";
import {
  barContainer,
  barProgress,
  barWithLabelWrapper,
  barWrapper,
  marker,
  thresholdContainer,
  thresholdContainerWithMarkers
} from "./ProgressBar.css";

export type ProgressBarVariant =
  | "stepped"
  | "labeled"
  | "threshold"
  | "multi-threshold";

type BaseProgressBarProps = {
  totalSteps?: number;
  currentStep?: number;
};

type SteppedVariantProps = BaseProgressBarProps & {
  variant: "stepped";
  label?: never;
  current?: never;
  threshold?: never;
  totalSteps: number;
  currentStep: number;
};

type LabeledVariantProps = BaseProgressBarProps & {
  variant: "labeled";
  label: string;
  current?: never;
  threshold?: never;
  totalSteps: number;
  currentStep: number;
};

type ContinuousVariantProps = {
  variant: "continuous"
  current: number
  max: number
}

type Tick = {
  active: boolean,
  text: string | undefined,
  showIcon: boolean,
  title: string,
  offset: number,
}

type TickedVariantProp = {
  variant: "ticked"
  current: number
  max: number
  ticks: Tick[]
}

export type ProgressBarProps =
  | SteppedVariantProps
  | LabeledVariantProps
  | ContinuousVariantProps
  | TickedVariantProp;

const toTwoDigits = (number: number) => String(number).padStart(2, "0");

type ProgressBarElementProps = {
  current: number;
  max: number;
  className?: string;
};

const ProgressBarElement = ({
  current,
  max,
  className
}: ProgressBarElementProps) => {
  const percentage = Math.min(Math.max(current / max, 0), 1) * 100;

  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={Math.min(Math.max(current, 0), max)}
      className={className || barProgress}
      data-testid="progress-bar"
      style={{ width: `${percentage}%` }}
    />
  );
};

/**
 * Displays a progress bar showing the current step relative to total steps.
 *
 * ## Variant System
 *
 * ### Stepped Variant (`variant="stepped"`)
 * Shows numeric step counts (e.g., "03" of "05") alongside the progress bar.
 * This is the default behavior for displaying progress with numerical indicators.
 *
 * ### Labeled Variant (`variant="labeled"`)
 * Shows a custom label above the progress bar without numeric step indicators.
 * Useful when you want to display descriptive text instead of step numbers.
 *
 * ### Threshold Variant (`variant="threshold"`)
 * Shows a progress bar with a single threshold marker. Uses the same div-based progress bar element as other variants.
 * Useful for showing progress towards a specific goal or target.
 *
 * ### Multi-Threshold Variant (`variant="multi-threshold"`)
 * Shows a progress bar with multiple threshold markers. Uses the same div-based progress bar element as other variants.
 * Useful for showing progress through multiple stages or milestones.
 *
 * @param {ProgressBarProps} props - Props for the ProgressBar component.
 * @param {ProgressBarVariant} props.variant - The variant of the progress bar.
 * @param {number} [props.totalSteps] - Total number of steps (for stepped/labeled variants).
 * @param {number} [props.currentStep] - Current step (for stepped/labeled variants).
 * @param {number} [props.current] - Current progress value.
 * @param {Tick[]} [props.ticks] - Ticks for progress (for ticked variants).
 * @param {string} [props.label] - Label for the labeled variant (required when variant is "labeled").
 * @returns {JSX.Element | null} The rendered progress bar.
 */
export const ProgressBar = (props: ProgressBarProps) => {
  // Handle threshold variants
  if (props.variant === "continuous" || props.variant === "ticked") {

    if (props.max <= 0) {
      return null;
    }

    const containerClass = props.variant === "ticked"
      ? thresholdContainerWithMarkers
      : thresholdContainer;

    return (
      <div className={containerClass}>
        <div className={barContainer}>
          <ProgressBarElement current={props.current} max={props.max} />
        </div>

        {props.variant === "ticked" &&
          props.ticks.map((tick, index) => {
            return (
              <Marker
                key={index}
                size={20}
                active={tick.active}
                text={tick.text}
                showIcon={tick.showIcon}
                className={marker}
                options={{ title: tick.title }}
                style={{ left: `${tick.offset}%` }}
              />
            );
          })}
      </div>
    );
  }

  // Handle stepped and labeled variants
  const { currentStep, totalSteps } = props as
    | SteppedVariantProps
    | LabeledVariantProps;

  if (props.variant === "labeled") {
    const { label } = props as LabeledVariantProps;
    return (
      <div className={barWithLabelWrapper}>
        <Typography textAlign="left" variant="body-sm">
          {label}
        </Typography>
        <div className={barWrapper}>
          <div className={barContainer}>
            <ProgressBarElement current={currentStep} max={totalSteps} />
          </div>
        </div>
      </div>
    );
  }

  // stepped variant
  return (
    <div className={barWrapper}>
      <Typography variant="subheader-6">{toTwoDigits(currentStep)}</Typography>
      <div className={barContainer}>
        <ProgressBarElement current={currentStep} max={totalSteps} />
      </div>
      <Typography variant="subheader-6">{toTwoDigits(totalSteps)}</Typography>
    </div>
  );
};
